import { Box } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { startTransition } from "react";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

interface DatePickerWithSmallLabelProps {
  label?: string;
  value?: Date | string | null;
  onChange?: (date: Date | string | null) => void;
  maxDate?: Date;
  minDate?: Date;
  error?: any;
  helperText?: string;
  [key: string]: any;
}

const DatePickerWithSmallLabel = ({
  label,
  value,
  onChange,
  maxDate,
  minDate,
  error,
  helperText,
  ...props
}: DatePickerWithSmallLabelProps) => {
  const customLabelStyle = {
    ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
    color: nawyTheme.nawy.colors.semantic.text.secondary,
  };

  // Convert value to Date object if it's a string, or null if empty
  const dateValue = value
    ? typeof value === "string"
      ? new Date(value)
      : value
    : null;

  // Set minimum date to today if not provided, to disable dates before today
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day to ensure proper comparison
  const effectiveMinDate = minDate || today;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {label && <CustomLabel sx={customLabelStyle}>{label}</CustomLabel>}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          views={["year", "month", "day"]}
          format="dd/MM/yyyy"
          value={dateValue}
          onChange={(date) => {
            if (onChange) {
              startTransition(() => {
                onChange(date);
              });
            }
          }}
          maxDate={maxDate}
          minDate={effectiveMinDate}
          slotProps={{
            // Tooltip-level so the calendar clears dialogs hosted above drawers
            // (e.g. the Add Task modal at zIndex 1450 over the zoom-call drawer).
            popper: { sx: { zIndex: 1500 } },
            textField: {
              error: !!error,
              helperText: helperText,
              fullWidth: true,
              size: "small",
              sx: {
                "& .MuiInputBase-root": {
                  height: "36px",
                  minHeight: "36px",
                },
                "& .MuiInputBase-input": {
                  padding: "8px 14px",
                },
              },
              ...props,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DatePickerWithSmallLabel;
